import {HalImageRef} from "@cm/hal/hal-ref"
import {DataType} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/basic-definitions"
import {Descriptor, MipLevelRegion, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {
    ImageOpHalOutputImage,
    SubSampleRegionFunction,
    WriteOutputImageRegionFunction,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-output-image"
import {RegionLockedHalImageRef} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/region-locked-hal-image-ref"
import {TextureEditorSettings} from "@app/textures/texture-editor/texture-editor-settings"
import {dataIntegrityChecker} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/data-integrity-checker"
import {getMipLevelSize} from "@cm/hal/hal-image/utils"
import {ExtUpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"

export class ImageOpHalOutputImagePhysical extends ImageOpHalOutputImage {
    constructor(ctx: ImageOpContextHal, descriptor: Descriptor, physicalDataType: DataType) {
        super(ctx, descriptor)
        const numMipLevels = 1 // TODO: support mipmaps for physical images
        this._outputImagePtr = this.ctx.createImagePhysical(descriptor, {dataType: physicalDataType})
        this._physicalOutputImageMipLevelWritePromise = new Array(numMipLevels).fill(null)
    }

    protected override dispose() {
        this._outputImagePtr?.release()
        this._outputImagePtr = null
        super.dispose()
    }

    get outputImagePtr(): HalImageRef {
        if (!this._outputImagePtr) {
            throw new Error("Attempting to access disposed outputImagePtr")
        }
        return this._outputImagePtr
    }

    invalidateRegions(_regions: Regions): void {
        this._physicalOutputImageMipLevelWritePromise.fill(null) // since we are invalidating regions, we need to mark all mip levels to be written again
    }

    async getUpdatedHalImage(
        writeRegionFn: WriteOutputImageRegionFunction,
        _subSampleRegionFn: SubSampleRegionFunction,
        mipLevelRegion: MipLevelRegion,
        options?: ExtUpdateImageOptions,
    ): Promise<RegionLockedHalImageRef> {
        const outputImagePtr = this.outputImagePtr

        outputImagePtr.addRef("ImageOpHalOutputImagePhysical.getUpdatedHalImage")

        // we always update the entire mip level for physical images
        mipLevelRegion = {
            x: 0,
            y: 0,
            ...getMipLevelSize(this.descriptor, mipLevelRegion.mipLevel),
            mipLevel: mipLevelRegion.mipLevel,
        }

        if (mipLevelRegion.width * mipLevelRegion.height > TextureEditorSettings.RegionPixelsWarnLimit) {
            this.ctx.logger.warn(
                `Large region size ${mipLevelRegion.width * mipLevelRegion.height} for mip level ${mipLevelRegion.mipLevel} on object ${this.debugId}`,
                this,
            )
        }

        if (!this._physicalOutputImageMipLevelWritePromise[mipLevelRegion.mipLevel]) {
            this.ctx.logger.trace(
                `Starting to write region [${mipLevelRegion.x}, ${mipLevelRegion.y}, ${mipLevelRegion.width}, ${mipLevelRegion.height}] on mip level ${mipLevelRegion.mipLevel} for object ${this.debugId}`,
                this,
            )
            const start = performance.now()
            const mipLevel = mipLevelRegion.mipLevel
            const writePromise = writeRegionFn(outputImagePtr.ref, mipLevelRegion, options).then(
                () => {
                    this.ctx.logger.trace(
                        `Completed to write region [${mipLevelRegion.x}, ${mipLevelRegion.y}, ${mipLevelRegion.width}, ${mipLevelRegion.height}] on mip level ${mipLevel} to physical image in ${performance.now() - start}ms for HalImage ${this.debugId}`,
                        this,
                    )
                },
                (e) => {
                    // Clear the cached promise so the next request can retry rather than permanently failing
                    this._physicalOutputImageMipLevelWritePromise[mipLevel] = null
                    throw e
                },
            )
            this._physicalOutputImageMipLevelWritePromise[mipLevel] = writePromise // store the write promise so subsequent requests can wait for it too
        }
        try {
            await this._physicalOutputImageMipLevelWritePromise[mipLevelRegion.mipLevel]
        } catch (e) {
            outputImagePtr.release("ImageOpHalOutputImagePhysical.getUpdatedHalImage")
            throw e
        }
        if (dataIntegrityChecker && this.debugId) {
            const data = outputImagePtr.ref.readImageDataFloat()
            await dataIntegrityChecker.check(this.debugId, data)
        }

        const updatedRegion = RegionLockedHalImageRef.fromHalImagePhysicalRef(this, mipLevelRegion)
        outputImagePtr.release("ImageOpHalOutputImagePhysical.getUpdatedHalImage")
        return updatedRegion
    }

    private _outputImagePtr: HalImageRef | null
    private _physicalOutputImageMipLevelWritePromise: (Promise<void> | null)[] // the write promise to the physical output image's mip-level
}
